import { useState } from "react";
import { ChevronDown, ChevronUp, Copy, Eye, EyeOff, Trash2 } from "lucide-react";
import { buildSeo } from "@baze/engine";
import { HANDLE_PATTERN, PHONE_PATTERN, SAFE_HREF_PATTERN, SECTION_META, SECTION_TONES, SECTION_VARIANTS, WA_PATTERN, WEEKDAYS, type SectionNode, type SectionTone, type WebsiteDefinition } from "@baze/schema";
import type { PublishIssue } from "@baze/api-client";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/select";
import { Switch, SwitchRow } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { t } from "@/i18n/id";
import { cn } from "@/lib/cn";
import { infoCopy } from "@/features/wizard/copy";
import { EVENT_INDUSTRIES, INVITATION_INDUSTRIES } from "@/features/sites/overview/context";
import { ImageField, SectionForm } from "./content-form";
import { sectionHeading, sectionIcons } from "./outline";
import { isSingleton, renamePage, setHidden, setTone, setVariant, toggleNav, updatePageSeo, updateSectionField, updateSite } from "./model/ops";

type Apply = (next: WebsiteDefinition, key?: string) => void;
type Tab = "section" | "info" | "seo";

export function Inspector({
  siteId,
  siteUrl,
  def,
  pageId,
  section,
  apply,
  onMove,
  onDuplicate,
  onDelete,
  focusSection = 0,
  focusInfo = 0,
  issues,
}: {
  siteId: string;
  siteUrl?: string;
  /** Changing this number switches to the Section tab (mobile "Edit isi"). */
  focusSection?: number;
  /** Changing this number switches to the Info tab (WhatsApp and contact fixes). */
  focusInfo?: number;
  /** What the publish check flags in the selected section (template filler, empty lists…). */
  issues?: PublishIssue[];
  def: WebsiteDefinition;
  pageId: string;
  section?: SectionNode;
  apply: Apply;
  onMove: (id: string, delta: -1 | 1) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  const sectionId = section?.id ?? null;
  const [tab, setTab] = useState<Tab>(sectionId ? "section" : "info");
  const [seenSection, setSeenSection] = useState(sectionId);
  const [seenFocus, setSeenFocus] = useState(focusSection);
  const [seenInfo, setSeenInfo] = useState(focusInfo);
  if (focusInfo !== seenInfo) {
    setSeenInfo(focusInfo);
    setTab("info");
  }
  if (sectionId !== seenSection) {
    setSeenSection(sectionId);
    setTab(sectionId ? "section" : "info");
  }
  if (focusSection !== seenFocus) {
    setSeenFocus(focusSection);
    if (sectionId) setTab("section");
  }

  const page = def.pages.find((p) => p.id === pageId) ?? def.pages[0]!;

  return (
    <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)} className="flex h-full flex-col">
      <div className="border-b border-line px-3 py-3">
        <TabsList className="flex w-full">
          <TabsTrigger className="flex-1 px-2" value="section" disabled={!section}>
            {t.editor.tabsShort.sections}
          </TabsTrigger>
          <TabsTrigger className="flex-1 px-2" value="info">{t.editor.tabsShort.info}</TabsTrigger>
          <TabsTrigger className="flex-1 px-2" value="seo">{t.editor.tabsShort.seo}</TabsTrigger>
        </TabsList>
      </div>
      <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto px-4 py-4">
        <TabsContent value="section" className="outline-none">
          {section && issues?.length ? (
            <div className="mb-4 space-y-2">
              {issues.map((issue, i) => (
                <Alert key={`${issue.code}-${i}`} tone="warning">
                  {issue.message}
                </Alert>
              ))}
            </div>
          ) : null}
          {section ? (
            <SectionPanel siteId={siteId} def={def} section={section} apply={apply} onMove={onMove} onDuplicate={onDuplicate} onDelete={onDelete} onOpenInfo={() => setTab("info")} />
          ) : (
            <div className="rounded-2xl bg-slate-50 px-4 py-5 ring-1 ring-slate-900/5">
              <p className="text-sm font-semibold text-slate-900">{t.editor.inspector.noSelection}</p>
              <p className="mt-1 text-sm leading-relaxed text-slate-500">{t.editor.inspector.noSelectionBody}</p>
            </div>
          )}
        </TabsContent>
        <TabsContent value="info" className="outline-none">
          <BusinessForm siteId={siteId} def={def} apply={apply} />
        </TabsContent>
        <TabsContent value="seo" className="outline-none">
          <SeoForm def={def} pageId={page.id} siteUrl={siteUrl} apply={apply} />
        </TabsContent>
      </div>
    </Tabs>
  );
}

function SectionPanel({
  siteId,
  def,
  section,
  apply,
  onMove,
  onDuplicate,
  onDelete,
  onOpenInfo,
}: {
  siteId: string;
  def: WebsiteDefinition;
  section: SectionNode;
  apply: Apply;
  onMove: (id: string, delta: -1 | 1) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
  onOpenInfo: () => void;
}) {
  const meta = SECTION_META[section.type];
  const TypeIcon = sectionIcons[section.type];
  const pinned = section.type === "navbar" || section.type === "footer";
  const variants = SECTION_VARIANTS[section.type];
  const singleton = isSingleton(section.type);
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-primary-soft text-primary">
          <TypeIcon className="size-4" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <p className="font-semibold text-slate-900">{meta.label}</p>
          <p className="text-[13px] leading-snug text-slate-500">{meta.description}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        <Button variant="secondary" size="sm" disabled={pinned} onClick={() => onMove(section.id, -1)}>
          <ChevronUp aria-hidden="true" />
          {t.editor.toolbar.moveUp}
        </Button>
        <Button variant="secondary" size="sm" disabled={pinned} onClick={() => onMove(section.id, 1)}>
          <ChevronDown aria-hidden="true" />
          {t.editor.toolbar.moveDown}
        </Button>
        <Button variant="secondary" size="sm" onClick={() => apply(setHidden(def, section.id, !section.hidden))}>
          {section.hidden ? <Eye aria-hidden="true" /> : <EyeOff aria-hidden="true" />}
          {section.hidden ? t.editor.toolbar.show : t.editor.toolbar.hide}
        </Button>
        {pinned || singleton ? null : (
          <Button variant="secondary" size="sm" onClick={() => onDuplicate(section.id)}>
            <Copy aria-hidden="true" />
            {t.editor.outline.duplicate}
          </Button>
        )}
        {pinned ? null : (
          <Button variant="danger-ghost" size="sm" onClick={() => onDelete(section.id)}>
            <Trash2 aria-hidden="true" />
            {t.editor.toolbar.delete}
          </Button>
        )}
      </div>
      <p className="rounded-xl bg-primary-soft/60 px-3 py-2 text-[12.5px] leading-snug text-slate-600">{t.editor.inspector.inlineTip}</p>
      {pinned ? null : <MenuCard def={def} section={section} apply={apply} />}
      <section aria-label={t.editor.inspector.contentTitle} className="space-y-3">
        <h3 className="text-xs font-bold tracking-wide text-slate-500 uppercase">{t.editor.inspector.contentTitle}</h3>
        <SectionForm siteId={siteId} def={def} section={section} apply={apply} />
        {section.type === "navbar" ? <NavMenuEditor def={def} section={section} apply={apply} /> : null}
        {section.type === "opening-hours" || section.type === "location" || section.type === "contact" ? (
          <Button variant="soft" size="sm" onClick={onOpenInfo}>
            {t.editor.inspector.openInfo}
          </Button>
        ) : null}
      </section>
      <details className="group rounded-xl ring-1 ring-line">
        <summary className="flex cursor-pointer list-none items-center justify-between px-3 py-2.5 text-sm font-semibold text-slate-800 select-none">
          {t.editor.inspector.appearanceTitle}
          <ChevronDown className="size-4 text-slate-400 transition-transform group-open:rotate-180" aria-hidden="true" />
        </summary>
        <div className="space-y-4 border-t border-line px-3 py-3">
          {variants.length > 1 ? (
            <Field label={t.editor.inspector.variant}>
              <NativeSelect selectSize="sm" value={section.variant} onChange={(e) => apply(setVariant(def, section.id, e.target.value))}>
                {variants.map((v) => (
                  <option key={v} value={v}>
                    {meta.variantLabels[v] ?? v}
                  </option>
                ))}
              </NativeSelect>
            </Field>
          ) : null}
          <Field label={t.editor.inspector.tone}>
            <NativeSelect selectSize="sm" value={section.tone ?? "default"} onChange={(e) => apply(setTone(def, section.id, e.target.value as SectionTone))}>
              {SECTION_TONES.map((tone) => (
                <option key={tone} value={tone}>
                  {t.editor.inspector.tones[tone]}
                </option>
              ))}
            </NativeSelect>
          </Field>
        </div>
      </details>
    </div>
  );
}

const NON_NAV = new Set(["navbar", "footer"]);

/** The section's link in the top menu, kept apart from its title so owners don't mistake one for the other. */
function MenuCard({ def, section, apply }: { def: WebsiteDefinition; section: SectionNode; apply: Apply }) {
  // Multi-page sites list pages, not sections (same rule as derivedNav in the engine).
  if (def.pages.length > 1) {
    return <p className="rounded-xl bg-slate-50 px-3 py-2 text-[12.5px] leading-snug text-slate-500 ring-1 ring-slate-900/5">{t.editor.inspector.menuPagesHint}</p>;
  }
  return (
    <section aria-label={t.editor.inspector.menuTitle} className="rounded-xl px-3 py-1 ring-1 ring-line">
      <SwitchRow
        id="show-nav"
        label={t.editor.inspector.showInNav}
        hint={section.showInNav && section.hidden ? t.editor.inspector.hiddenNotInNav : t.editor.inspector.showInNavHint}
        checked={!!section.showInNav}
        onCheckedChange={(v) => apply(toggleNav(def, section.id, v))}
      />
      {section.showInNav ? (
        <div className="pb-3">
          <Field label={t.editor.inspector.navLabel} hint={t.editor.inspector.navLabelHint} optional>
            <Input
              inputSize="sm"
              value={section.navLabel ?? ""}
              placeholder={SECTION_META[section.type].label}
              onChange={(e) => apply(updateSectionField(def, section.id, "navLabel", e.target.value || undefined), section.id + ":nav")}
            />
          </Field>
        </div>
      ) : null}
    </section>
  );
}

/** On the navbar: every menu link in one place, since the links themselves live on the sections (or pages). */
function NavMenuEditor({ def, section, apply }: { def: WebsiteDefinition; section: SectionNode; apply: Apply }) {
  const { navMenuTitle: title, navMenuHint: hint, navMenuPagesHint: pagesHint, navMenuEmpty: empty } = t.editor.inspector;
  if (def.pages.length > 1) {
    return (
      <div className="space-y-2 pt-1">
        <p className="text-sm font-semibold text-slate-800">{title}</p>
        <p className="text-[13px] leading-snug text-slate-500">{pagesHint}</p>
        <ul className="space-y-2">
          {def.pages.map((p) => (
            <li key={p.id}>
              <Input inputSize="sm" aria-label={p.title} value={p.title} onChange={(e) => apply(renamePage(def, p.id, e.target.value), `page:${p.id}:title`)} />
            </li>
          ))}
        </ul>
      </div>
    );
  }
  const page = def.pages.find((p) => p.sections.some((s) => s.id === section.id)) ?? def.pages[0]!;
  const candidates = page.sections.filter((s) => !NON_NAV.has(s.type));
  const shown = candidates.filter((s) => s.showInNav && !s.hidden).length;
  return (
    <div className="space-y-2 pt-1">
      <p className="text-sm font-semibold text-slate-800">{title}</p>
      <p className="text-[13px] leading-snug text-slate-500">{shown === 0 ? empty : hint}</p>
      <ul className="space-y-1.5">
        {candidates.map((s) => {
          const fallback = SECTION_META[s.type].label;
          const name = sectionHeading(s) ?? fallback;
          return (
            <li key={s.id} className={cn("rounded-xl px-3 py-2 ring-1", s.showInNav ? "bg-white ring-line" : "bg-slate-50 ring-slate-900/5")}>
              <div className="flex min-h-9 items-center justify-between gap-3">
                <label htmlFor={`nav-${s.id}`} className="min-w-0 flex-1 cursor-pointer">
                  <span className={cn("block truncate text-sm font-medium", s.showInNav ? "text-slate-800" : "text-slate-500")}>{name}</span>
                  {s.hidden ? <span className="block text-xs text-slate-400">{t.editor.inspector.hiddenNotInNav}</span> : null}
                </label>
                <Switch id={`nav-${s.id}`} checked={!!s.showInNav} onCheckedChange={(v) => apply(toggleNav(def, s.id, v))} />
              </div>
              {s.showInNav ? (
                <Input
                  inputSize="sm"
                  className="mt-1.5"
                  aria-label={`${t.editor.inspector.navLabel}: ${name}`}
                  value={s.navLabel ?? ""}
                  placeholder={fallback}
                  onChange={(e) => apply(updateSectionField(def, s.id, "navLabel", e.target.value || undefined), s.id + ":nav")}
                />
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function BusinessForm({ siteId, def, apply }: { siteId: string; def: WebsiteDefinition; apply: Apply }) {
  const site = def.site;
  const set = (path: string, value: unknown) => apply(updateSite(def, path, value), `site:${path}`);
  // Labels follow the kind of site, as in the wizard ("Nama mempelai", "Nama acara").
  const copy = infoCopy(site.industry);
  const invitation = INVITATION_INDUSTRIES.has(site.industry);
  const event = EVENT_INDUSTRIES.has(site.industry);
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-sm font-bold text-slate-900">{invitation ? t.editor.info.titleInvitation : event ? t.editor.info.titleEvent : t.editor.info.title}</h2>
        <p className="mt-1 text-[13px] leading-relaxed text-slate-500">{event ? t.editor.info.subtitleEvent : t.editor.info.subtitle}</p>
      </div>
      <Field label={copy.name}>
        <Input value={site.name} onChange={(e) => set("name", e.target.value)} />
      </Field>
      <Field label={t.editor.info.tagline} optional>
        <Input value={site.tagline ?? ""} onChange={(e) => set("tagline", e.target.value || undefined)} />
      </Field>
      <ImageField
        siteId={siteId}
        label={t.editor.info.logo}
        hint={t.editor.info.logoHint}
        uploaded={t.editor.info.logoUploaded}
        fit="contain"
        value={site.logo}
        onChange={(v) => set("logo", v ? { ...v, alt: v.alt || site.name } : undefined)}
      />
      <WaField value={site.contact.whatsapp ?? ""} onChange={(v) => set("contact.whatsapp", v)} />
      <LooseField label={t.editor.info.phone} value={site.contact.phone ?? ""} pattern={PHONE_PATTERN} onChange={(v) => set("contact.phone", v)} />
      <LooseField label={t.editor.info.email} type="email" value={site.contact.email ?? ""} pattern={/^[^\s@]+@[^\s@]+\.[^\s@]+$/} onChange={(v) => set("contact.email", v)} />
      <Field label={t.editor.info.city} optional>
        <Input value={site.contact.city ?? ""} onChange={(e) => set("contact.city", e.target.value || undefined)} />
      </Field>
      <Field label={t.editor.info.address} optional>
        <Textarea rows={2} value={site.contact.address ?? ""} onChange={(e) => set("contact.address", e.target.value || undefined)} />
      </Field>
      <LooseField label={t.editor.info.mapsUrl} hint={t.editor.info.mapsUrlHint} value={site.contact.mapsUrl ?? ""} pattern={SAFE_HREF_PATTERN} placeholder="https://" onChange={(v) => set("contact.mapsUrl", v)} />
      <Field label={t.editor.info.waMessage} hint={t.editor.info.waMessageHint} optional>
        <Textarea rows={2} value={site.whatsapp?.defaultMessage ?? ""} onChange={(e) => set("whatsapp.defaultMessage", e.target.value || undefined)} />
      </Field>
      <div className="space-y-2">
        <p className="text-sm font-semibold text-slate-800">{t.editor.info.social}</p>
        {(["instagram", "tiktok", "facebook", "youtube"] as const).map((network) => (
          <LooseField
            key={network}
            label={t.editor.info.socialNetworks[network]}
            value={site.social[network] ?? ""}
            pattern={HANDLE_PATTERN}
            placeholder={t.editor.info.socialPlaceholder}
            normalize={(v) => v.replace(/^@/, "")}
            onChange={(v) => set(`social.${network}`, v)}
          />
        ))}
      </div>
      {/* Opening hours mean nothing on an invitation; keep the form only to clear hours set earlier. */}
      {!event || (site.hours?.length ?? 0) > 0 ? <HoursForm def={def} apply={apply} /> : null}
    </div>
  );
}

function WaField({ value, onChange }: { value: string; onChange: (v: string | undefined) => void }) {
  const [text, setText] = useState(value);
  const [invalid, setInvalid] = useState(false);
  const [synced, setSynced] = useState(value);
  if (value !== synced) {
    setSynced(value);
    setText(value);
    setInvalid(false);
  }
  const commit = (raw: string) => {
    const digits = raw.replace(/\D/g, "");
    const normalized = digits.startsWith("0") ? `62${digits.slice(1)}` : digits.startsWith("8") ? `62${digits}` : digits;
    if (!raw.trim()) {
      setInvalid(false);
      onChange(undefined);
      return;
    }
    if (WA_PATTERN.test(normalized)) {
      setText(normalized);
      setInvalid(false);
      onChange(normalized);
      return;
    }
    setInvalid(true);
  };
  return (
    <Field label={t.editor.info.whatsapp} hint={invalid ? undefined : t.editor.whatsappHint} error={invalid ? t.editor.whatsappInvalid : undefined} optional>
      <Input
        inputMode="tel"
        value={text}
        placeholder="6281234567890"
        onChange={(e) => {
          setText(e.target.value);
          setInvalid(false);
          const digits = e.target.value.replace(/\D/g, "");
          const normalized = digits.startsWith("0") ? `62${digits.slice(1)}` : digits.startsWith("8") ? `62${digits}` : digits;
          if (!e.target.value.trim()) onChange(undefined);
          else if (WA_PATTERN.test(normalized)) onChange(normalized);
        }}
        onBlur={() => commit(text)}
      />
    </Field>
  );
}

function HoursForm({ def, apply }: { def: WebsiteDefinition; apply: Apply }) {
  const hours = def.site.hours ?? [];
  const setHours = (next: NonNullable<WebsiteDefinition["site"]["hours"]>) => apply(updateSite(def, "hours", next), "site:hours");
  if (hours.length === 0) {
    return (
      <div className="rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-900/5">
        <p className="text-sm font-semibold text-slate-900">{t.editor.info.hours}</p>
        <p className="mt-1 text-[13px] text-slate-500">{t.editor.info.hoursEmpty}</p>
        <Button
          className="mt-3"
          variant="secondary"
          size="sm"
          onClick={() => setHours(WEEKDAYS.map((day) => ({ day, closed: day === "sun", ...(day === "sun" ? {} : { open: "08:00", close: "21:00" }) })))}
        >
          {t.editor.info.hoursAdd}
        </Button>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <p className="text-sm font-semibold text-slate-800">{t.editor.info.hours}</p>
      {WEEKDAYS.map((day) => {
        const row = hours.find((h) => h.day === day) ?? { day, closed: true as const };
        const write = (patch: { closed?: boolean; open?: string; close?: string }) => {
          const next = { ...row, ...patch };
          const clean = next.closed ? { day, closed: true } : { day, closed: false, open: next.open || "08:00", close: next.close || "17:00" };
          const rest = hours.filter((h) => h.day !== day);
          setHours(WEEKDAYS.map((d) => (d === day ? clean : rest.find((h) => h.day === d) ?? { day: d, closed: true })));
        };
        return (
          <div key={day} className="rounded-xl bg-slate-50 px-3 py-2 ring-1 ring-slate-900/5">
            <SwitchRow id={`hours-${day}`} label={t.editor.info.days[day]} checked={!row.closed} onCheckedChange={(open) => write({ closed: !open })} />
            {row.closed ? null : (
              <div className="grid grid-cols-2 gap-2 pb-2">
                <Field label={t.editor.info.openTime}>
                  <Input type="time" value={"open" in row ? row.open ?? "08:00" : "08:00"} onChange={(e) => write({ open: e.target.value })} />
                </Field>
                <Field label={t.editor.info.closeTime}>
                  <Input type="time" value={"close" in row ? row.close ?? "17:00" : "17:00"} onChange={(e) => write({ close: e.target.value })} />
                </Field>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function LooseField({
  label,
  value,
  pattern,
  onChange,
  hint,
  placeholder,
  type,
  normalize,
}: {
  label: string;
  value: string;
  pattern: RegExp;
  onChange: (v: string | undefined) => void;
  hint?: string;
  placeholder?: string;
  type?: string;
  normalize?: (v: string) => string;
}) {
  const [text, setText] = useState(value);
  const [invalid, setInvalid] = useState(false);
  const [synced, setSynced] = useState(value);
  if (value !== synced) {
    setSynced(value);
    setText(value);
    setInvalid(false);
  }
  return (
    <Field label={label} hint={invalid ? undefined : hint} error={invalid ? t.editor.invalidField : undefined} optional>
      <Input
        type={type}
        placeholder={placeholder}
        value={text}
        onChange={(e) => {
          const next = normalize ? normalize(e.target.value) : e.target.value;
          setText(next);
          if (!next.trim()) {
            setInvalid(false);
            onChange(undefined);
          } else if (pattern.test(next)) {
            setInvalid(false);
            onChange(next);
          }
        }}
        onBlur={() => setInvalid(!!text.trim() && !pattern.test(text))}
      />
    </Field>
  );
}

function SeoForm({ def, pageId, siteUrl, apply }: { def: WebsiteDefinition; pageId: string; siteUrl?: string; apply: Apply }) {
  const page = def.pages.find((p) => p.id === pageId) ?? def.pages[0]!;
  const host = siteUrl ? siteUrl.replace(/^https?:\/\//, "").replace(/\/$/, "") : "";
  // Same fallbacks as the published site: empty fields are filled from the site name, tagline and page copy.
  const auto = buildSeo(def, { ...page, seo: { ...page.seo, title: "", description: "" } }, { canonicalOrigin: siteUrl ?? "https://example.com" });
  const shownTitle = page.seo.title || auto.title;
  const shownDescription = page.seo.description || auto.description;
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-sm font-bold text-slate-900">{t.editor.seo.title}</h2>
        <p className="mt-1 text-[13px] leading-relaxed text-slate-500">{t.editor.seo.subtitle}</p>
      </div>
      <Field label={t.editor.seo.pageTitleLabel}>
        <Input value={page.title} onChange={(e) => apply(renamePage(def, page.id, e.target.value), "page:title")} />
      </Field>
      <Field label={t.editor.seo.pageTitle} count={{ value: page.seo.title.length, max: 70 }}>
        <Input value={page.seo.title} placeholder={auto.title} onChange={(e) => apply(updatePageSeo(def, page.id, { title: e.target.value.slice(0, 70) }), "page:seo-title")} />
      </Field>
      <Field label={t.editor.seo.pageDescription} count={{ value: page.seo.description.length, max: 170 }}>
        <Textarea rows={3} value={page.seo.description} placeholder={auto.description} onChange={(e) => apply(updatePageSeo(def, page.id, { description: e.target.value.slice(0, 170) }), "page:seo-desc")} />
      </Field>
      <div>
        <p className="text-sm font-semibold text-slate-800">{t.editor.seo.preview}</p>
        <div className="mt-2 rounded-2xl bg-white p-4 ring-1 ring-slate-900/10">
          <p className="truncate text-[13px] text-slate-500">{host}{page.slug === "/" ? "" : page.slug}</p>
          <p className="truncate text-base font-medium text-[#1a0dab]">{shownTitle}</p>
          <p className="line-clamp-2 text-sm text-slate-600">{shownDescription || <span className="text-slate-400 italic">{t.editor.seo.noDescription}</span>}</p>
        </div>
        {!page.seo.title || !page.seo.description ? <p className="mt-2 text-xs leading-relaxed text-slate-500">{t.editor.seo.autoHint}</p> : null}
      </div>
    </div>
  );
}
